import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';

import { LoggerService } from '../logging/logger.service';
import { MemoryStorage } from './memory-storage';
import type { StorageKind, StorageLike } from './storage.models';

/**
 * The only place in the application that touches Web Storage.
 *
 * Access is defensive on purpose: Safari private mode, embedded webviews and
 * hardened enterprise browsers all throw on `localStorage` access, and a
 * theme preference is never worth crashing the application for.
 */
@Injectable({ providedIn: 'root' })
export class StorageService {
  private readonly window = inject(DOCUMENT).defaultView;
  private readonly logger = inject(LoggerService);
  private readonly fallback = new MemoryStorage();

  /** Resolve a backend, degrading to memory when the platform refuses. */
  backend(kind: StorageKind): StorageLike {
    if (kind === 'memory') {
      return this.fallback;
    }

    try {
      const candidate = kind === 'local' ? this.window?.localStorage : this.window?.sessionStorage;

      if (!candidate) {
        return this.fallback;
      }

      // Touch the API: merely reading the property does not throw in Safari.
      const probe = '__celeri_probe__';
      candidate.setItem(probe, probe);
      candidate.removeItem(probe);

      return candidate;
    } catch {
      this.logger.warn(`Web Storage "${kind}" unavailable, falling back to memory.`);

      return this.fallback;
    }
  }

  read(key: string, kind: StorageKind = 'local'): string | null {
    try {
      return this.backend(kind).getItem(key);
    } catch {
      return null;
    }
  }

  write(key: string, value: string, kind: StorageKind = 'local'): void {
    try {
      this.backend(kind).setItem(key, value);
    } catch {
      this.logger.warn(`Unable to persist "${key}".`);
    }
  }

  remove(key: string, kind: StorageKind = 'local'): void {
    try {
      this.backend(kind).removeItem(key);
    } catch {
      /* nothing sensible to do */
    }
  }

  /** Read and parse JSON, returning `null` when absent or corrupted. */
  readJson<T>(key: string, kind: StorageKind = 'local'): T | null {
    const raw = this.read(key, kind);

    if (raw === null) {
      return null;
    }

    try {
      return JSON.parse(raw) as T;
    } catch {
      this.logger.warn(`Discarding corrupted storage entry "${key}".`);
      this.remove(key, kind);

      return null;
    }
  }

  writeJson<T>(key: string, value: T, kind: StorageKind = 'local'): void {
    this.write(key, JSON.stringify(value), kind);
  }
}
